import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, Subject, forkJoin, of} from 'rxjs';
import {catchError, take, takeUntil} from 'rxjs/operators';
import {Select} from '@ngxs/store';
import {environment} from '@env/environment';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {PrinterClientService} from '@app/service/printer-client.service';
import {SocketIoService} from '@app/service/socket-io.service';
import {EventEnum} from '@app/enums/event.enum';
import {LoginService} from '@app/service/login.service';
import {SesionState} from '@app/state/sesion/sesion.state';
import {Office} from '@app/domain/office';
import {
    LocalPrinterClientStatus,
    PrinterClient,
    PrinterClientBlock,
    PrinterClientCommand,
    PrinterClientCommandResult,
    PrinterPairingCode
} from '@app/domain/printer-client';

interface PrinterClientGroup {
    officeCode: string;
    clients: PrinterClient[];
    /** Jars antiguos no bloqueados: si hay más de uno, recibe el que se conectó primero. */
    legacyActive: number;
}

/**
 * Panel "Clientes de impresión": qué PCs (jars) están conectadas por punto de venta, con qué impresoras, y acciones
 * remotas (probar, marcar principal, reiniciar, desactivar; bloquear los jars antiguos que no aceptan comandos).
 */
@Component({
    selector: 'app-printer-clients-dialog',
    templateUrl: './printer-clients-dialog.component.html',
    styleUrls: ['./printer-clients-dialog.component.scss']
})
export class PrinterClientsDialogComponent implements OnInit, OnDestroy {

    @Select(SesionState.getOffice) office$: Observable<Office>;

    /** Marca para los textos (Teki / Cbetcloud): el mismo servicio de impresión sirve a las dos. */
    readonly brand: string = environment.company || 'Teki';
    /** Instalador para Windows, siempre la última versión. Vacío = la marca no tiene instalador (sin botón). */
    readonly installerUrl: string = environment.printerInstallerUrl || '';

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    groups: PrinterClientGroup[] = [];
    blocks: PrinterClientBlock[] = [];
    loading = false;
    loaded = false;
    /** socketId con una acción en curso, para deshabilitar sus botones. */
    busySocketId: string = null;
    testMenuItems: MenuItem[] = [];
    /** Equipos vinculados con código (backend): solo esos se pueden quitar revocando su token. */
    pairedDeviceIds = new Set<string>();

    /** Vista "Agregar equipo". */
    adding = false;
    offices: Office[] = [];
    officeCode: string;
    pairingCode: PrinterPairingCode = null;
    /** El ícono de copiar pasa a ✓ unos segundos después de copiar. */
    codeCopied = false;
    generating = false;
    /** Servicio de impresión instalado en esta PC; null si no se detectó. */
    localClient: LocalPrinterClientStatus = null;
    detectingLocal = false;
    pairingLocal = false;

    private unsubscribe$ = new Subject<void>();

    constructor(private printerClientService: PrinterClientService,
                private socketIoService: SocketIoService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private loginService: LoginService) {
    }

    ngOnInit(): void {
        this.socketIoService.onEvent(EventEnum.PRINTER_CLIENTS_CHANGED)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                if (this.visible) {
                    this.load();
                }
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    onShow(): void {
        this.adding = false;
        this.load();
    }

    close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    load(): void {
        this.loading = true;
        forkJoin({
            list: this.printerClientService.findAll(),
            // Sin el backend actualizado solo se pierde la opción "Quitar equipo"
            devices: this.printerClientService.findPairedDevices().pipe(catchError(() => of([])))
        }).subscribe(({list, devices}) => {
            this.groups = this.groupByOfficeCode(list.clients || []);
            this.blocks = list.blocks || [];
            this.pairedDeviceIds = new Set(devices.map(d => d.deviceId));
            this.loading = false;
            this.loaded = true;
        }, () => {
            this.loading = false;
        });
    }

    startAdding(): void {
        this.adding = true;
        this.pairingCode = null;
        this.offices = this.loginService.getPuntosVentaUsuario();
        this.office$.pipe(take(1)).subscribe(office => this.officeCode = office?.codigo || this.offices[0]?.codigo);
        this.detectLocalClient();
    }

    stopAdding(): void {
        this.adding = false;
        this.load();
    }

    detectLocalClient(): void {
        this.detectingLocal = true;
        this.printerClientService.localStatus().subscribe(status => {
            this.localClient = status;
            this.detectingLocal = false;
        }, () => {
            this.localClient = null;
            this.detectingLocal = false;
        });
    }

    generateCode(): void {
        this.generating = true;
        this.printerClientService.createPairingCode(this.officeCode).subscribe(code => {
            this.pairingCode = code;
            this.codeCopied = false;
            this.generating = false;
        }, () => this.generating = false);
    }

    copyCode(): void {
        const code = this.pairingCode?.code;
        if (!code) {
            return;
        }
        const done = () => {
            this.codeCopied = true;
            this.messageService.add({severity: 'success', summary: 'Código copiado', detail: code, life: 2000});
            setTimeout(() => this.codeCopied = false, 2000);
        };
        // navigator.clipboard solo existe en https/localhost; si no está, se copia con un textarea temporal
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(code).then(done, () => this.copyWithTextarea(code) && done());
        } else if (this.copyWithTextarea(code)) {
            done();
        }
    }

    private copyWithTextarea(text: string): boolean {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
    }

    /** Vincula el servicio de esta PC sin escribir el código: se genera y se envía al jar por localhost. */
    pairThisComputer(): void {
        this.pairingLocal = true;
        this.printerClientService.createPairingCode(this.officeCode).subscribe(code => {
            this.printerClientService.localPair(code.code).subscribe(resp => {
                this.pairingLocal = false;
                if (resp?.ok) {
                    this.messageService.add({severity: 'success', summary: 'Equipo vinculado',
                        detail: `${this.localClient?.deviceName} imprimirá para ${resp.officeCode}`});
                    this.stopAdding();
                } else {
                    this.messageService.add({severity: 'warn', summary: 'No se pudo vincular', detail: resp?.message});
                }
            }, () => {
                this.pairingLocal = false;
                this.messageService.add({severity: 'warn', summary: 'No se pudo vincular',
                    detail: 'El servicio de impresión de esta PC no respondió.'});
            });
        }, () => this.pairingLocal = false);
    }

    canRemove(client: PrinterClient): boolean {
        return !client.legacy && !!client.deviceId && this.pairedDeviceIds.has(client.deviceId);
    }

    /** Revoca el token del equipo y le pide al jar que borre su configuración. */
    remove(client: PrinterClient): void {
        this.confirmationService.confirm({
            message: `¿Quitar <b>${client.deviceName}</b>? Dejará de imprimir hasta que lo vincules con un código nuevo.`,
            accept: () => {
                this.busySocketId = client.socketId;
                this.printerClientService.revokeDevice(client.deviceId).subscribe(() => {
                    this.printerClientService.command(client.socketId, 'unlink').pipe(catchError(() => of(null)))
                        .subscribe(() => {
                            this.busySocketId = null;
                            this.messageService.add({severity: 'success', summary: 'Equipo quitado', detail: client.deviceName});
                            this.load();
                        });
                }, () => this.busySocketId = null);
            }
        });
    }

    openTestMenu(menu: any, event: Event, client: PrinterClient): void {
        this.testMenuItems = client.printers.map(printerName => ({
            label: printerName,
            icon: 'pi pi-print',
            command: () => this.runCommand(client, 'test-print', {printerName})
        }));
        menu.toggle(event);
    }

    toggleDefault(client: PrinterClient): void {
        this.runCommand(client, 'set-default', {isDefault: !client.isDefault});
    }

    restart(client: PrinterClient): void {
        this.runCommand(client, 'restart');
    }

    disable(client: PrinterClient): void {
        this.confirmationService.confirm({
            message: `¿Desactivar el cliente de impresión de <b>${client.deviceName}</b>? Dejará de recibir impresiones `
                + 'y solo se puede volver a iniciar desde la ventana del servicio en esa PC.',
            accept: () => this.runCommand(client, 'disable')
        });
    }

    block(client: PrinterClient): void {
        this.confirmationService.confirm({
            message: `¿Bloquear el cliente antiguo de <b>${client.officeCode}</b> (IP ${client.ip})? Seguirá conectado `
                + 'pero dejará de recibir impresiones. Se puede desbloquear desde este panel.',
            accept: () => {
                this.busySocketId = client.socketId;
                this.printerClientService.block(client.socketId).subscribe(() => {
                    this.busySocketId = null;
                    this.messageService.add({severity: 'success', summary: 'Cliente bloqueado', detail: client.officeCode});
                    this.load();
                }, () => this.busySocketId = null);
            }
        });
    }

    unblock(rule: PrinterClientBlock): void {
        this.printerClientService.unblock(rule.id).subscribe(() => {
            this.messageService.add({severity: 'success', summary: 'Bloqueo eliminado', detail: rule.officeCode});
            this.load();
        });
    }

    trackBySocket(index: number, client: PrinterClient): string {
        return client.socketId;
    }

    private runCommand(client: PrinterClient, command: PrinterClientCommand, payload?: object): void {
        this.busySocketId = client.socketId;
        this.printerClientService.command(client.socketId, command, payload).subscribe((resp: PrinterClientCommandResult) => {
            this.busySocketId = null;
            this.messageService.add({
                severity: resp?.ok ? 'success' : 'warn',
                summary: client.deviceName || client.officeCode,
                detail: resp?.message || (resp?.ok ? 'Listo' : 'No se pudo completar la acción')
            });
            this.load();
        }, () => this.busySocketId = null);
    }

    private groupByOfficeCode(clients: PrinterClient[]): PrinterClientGroup[] {
        const byCode = new Map<string, PrinterClient[]>();
        clients.forEach(client => {
            const list = byCode.get(client.officeCode) || [];
            list.push(client);
            byCode.set(client.officeCode, list);
        });
        return Array.from(byCode.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([officeCode, list]) => ({
                officeCode,
                clients: list,
                legacyActive: list.filter(c => c.legacy && !c.blocked).length
            }));
    }
}
